import { useMemo, useState } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import { ActionBar, DataGrid, StatusBar, type GridColumn } from '@/components/desktop';
import { ReverseButton, printDocument } from '@/components/documents';
import { SearchSelect } from '@/components/SearchSelect';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  OkNote,
  Pager,
  ScreenWithForm,
  Select,
  StatusPill,
  Table,
  TextArea,
  errorMessage,
  todayIso,
} from '@/components/ui';
import { formatDate, formatMoney, roundMoney } from '@/format';
import {
  useBankAccounts,
  useCashBoxes,
  useFinanceMutation,
  useOpenBills,
  useReceipts,
  type ReceiptRow,
} from '@/hooks/finance';
import { useBuyers } from '@/hooks/sales';
import { useAuth } from '@/stores/auth';
import { useFarmScope } from '@/stores/farmScope';

const PAGE = 50;

const STATUSES = [
  { value: '', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'POSTED', label: 'Posted' },
  { value: 'REVERSED', label: 'Reversed' },
];

const INSTRUMENTS = [
  { value: 'CASH', label: 'Cash' },
  { value: 'ONLINE', label: 'Online transfer' },
  { value: 'CHEQUE', label: 'Cheque' },
];

/**
 * Financials › Receipts.
 *
 * Money arriving from a buyer, and which bills it settles. "Allocate oldest first"
 * does the usual case in one click, because that is how a running account is
 * normally cleared.
 *
 * A bank charge is its own box, not something taken off the amount. The buyer paid
 * what they paid; the charge is the bank's, and netting the two would make the
 * buyer's account wrong by the size of the fee.
 */
export function Receipts() {
  const can = useAuth((s) => s.can);
  const farmIds = useFarmScope((s) => s.selectedFarmIds);

  const [status, setStatus] = useState('');
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<ReceiptRow | null>(null);
  const [buyerId, setBuyerId] = useState('');
  const [receivedOn, setReceivedOn] = useState(todayIso());
  const [amount, setAmount] = useState('');
  const [instrument, setInstrument] = useState('CASH');
  const [bankCharge, setBankCharge] = useState('');
  const [chequeNo, setChequeNo] = useState('');
  const [reference, setReference] = useState('');
  const [into, setInto] = useState('');
  const [allocations, setAllocations] = useState<Record<string, string>>({});
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const receipts = useReceipts({ status, take: PAGE, skip });
  const buyers = useBuyers();
  const cashBoxes = useCashBoxes(farmIds);
  const banks = useBankAccounts(farmIds);
  const openBills = useOpenBills(buyerId);
  const mutate = useFinanceMutation<unknown>();

  const rows = receipts.data?.items ?? [];
  const total = receipts.data?.total ?? 0;
  const current = rows.find((r) => r.id === selected) ?? null;

  /** Cash boxes and bank accounts in one picker, because the question is "where". */
  const places = useMemo(
    () => [
      ...(cashBoxes.data?.items ?? []).map((b) => ({
        value: `cash:${b.id}`,
        label: `${b.nameEn} — cash, ${b.farmName}`,
      })),
      ...(banks.data?.items ?? []).map((b) => ({
        value: `bank:${b.id}`,
        label: `${b.nameEn} — ${b.bankLabel ?? 'bank'}`,
      })),
    ],
    [cashBoxes.data, banks.data],
  );

  const allocatedTotal = useMemo(
    () =>
      // To the paisa, so 0.10 + 0.20 is not "more than" a 0.30 receipt.
      roundMoney(
        Object.values(allocations).reduce((sum, v) => {
          const n = Number(v);
          return sum + (Number.isFinite(n) ? n : 0);
        }, 0),
      ),
    [allocations],
  );
  const unapplied = useMemo(() => {
    const a = Number(amount);
    if (!Number.isFinite(a)) return null;
    return roundMoney(a - allocatedTotal).toFixed(2);
  }, [amount, allocatedTotal]);

  function reset() {
    setEditing(null);
    setBuyerId('');
    setReceivedOn(todayIso());
    setAmount('');
    setInstrument('CASH');
    setBankCharge('');
    setChequeNo('');
    setReference('');
    setInto('');
    setAllocations({});
    setRemarks('');
    setError('');
    setNote('');
  }

  function startEdit(row: ReceiptRow) {
    setEditing(row);
    setBuyerId(row.buyerId);
    setReceivedOn(row.receivedOn);
    setAmount(row.entryAmount);
    setInstrument(row.instrument);
    setBankCharge(row.bankCharge);
    setChequeNo(row.chequeNo ?? '');
    setReference(row.reference ?? '');
    setInto(
      row.intoCashBoxId
        ? `cash:${row.intoCashBoxId}`
        : row.intoBankAccountId
          ? `bank:${row.intoBankAccountId}`
          : '',
    );
    setAllocations(
      Object.fromEntries(row.allocations.map((a) => [a.dispatchBillId, a.lineAmount])),
    );
    setRemarks(row.remarks ?? '');
    setError('');
    setShowForm(true);
  }

  /** Fills the allocation boxes down the list until the money runs out. */
  function allocateOldestFirst() {
    let left = Number(amount);
    if (!Number.isFinite(left)) return;
    const next: Record<string, string> = {};
    for (const bill of openBills.data?.items ?? []) {
      if (left <= 0) break;
      const owed = Number(bill.outstanding);
      const put = Math.min(left, owed);
      next[bill.id] = put.toFixed(2);
      left -= put;
    }
    setAllocations(next);
  }

  function body() {
    const [kind, id] = into.split(':');
    const lines = Object.entries(allocations)
      .filter(([, v]) => v.trim() !== '' && Number(v) > 0)
      .map(([dispatchBillId, v]) => ({ dispatchBillId, amount: v }));

    return {
      buyerId,
      receivedOn,
      amount,
      instrument,
      ...(bankCharge.trim() ? { bankCharge } : {}),
      ...(chequeNo.trim() ? { chequeNo: chequeNo.trim() } : {}),
      ...(reference.trim() ? { reference: reference.trim() } : {}),
      ...(kind === 'cash' && id ? { intoCashBoxId: id } : {}),
      ...(kind === 'bank' && id ? { intoBankAccountId: id } : {}),
      ...(lines.length > 0 ? { allocations: lines } : {}),
      ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
    };
  }

  async function save() {
    setError('');
    setNote('');
    if (!buyerId) return setError('Pick the buyer');
    if (!amount || Number(amount) <= 0) return setError('Enter how much came in');
    if (allocatedTotal > Number(amount)) {
      return setError('The allocations add up to more than was received');
    }

    try {
      if (editing) {
        await mutate.mutateAsync(() =>
          api.patch(`/api/v1/fin/receipts/${editing.id}`, {
            version: editing.version,
            ...body(),
          }),
        );
      } else {
        await mutate.mutateAsync(() => api.post('/api/v1/fin/receipts', body()));
      }
      setNote(editing ? 'Receipt saved.' : 'Receipt recorded.');
      setShowForm(false);
      reset();
    } catch (e) {
      setError(errorMessage(e, 'That receipt could not be saved'));
    }
  }

  async function post(row: ReceiptRow) {
    setError('');
    try {
      await mutate.mutateAsync(() =>
        api.post(`/api/v1/fin/receipts/${row.id}/post`, { version: row.version }),
      );
      setNote(`${row.documentNo} posted.`);
    } catch (e) {
      setError(errorMessage(e, 'That receipt could not be posted'));
    }
  }

  function print(row: ReceiptRow) {
    printDocument({
      title: `Receipt ${row.documentNo}`,
      facts: [
        ['Receipt no', row.documentNo],
        ['Date', formatDate(row.receivedOn)],
        ['Received from', row.buyerName],
        ['Amount', formatMoney(row.entryAmount)],
        ['How', row.instrument],
        ['Into', row.intoName ?? '—'],
        ['Bank charge', formatMoney(row.bankCharge)],
        ['Applied to bills', formatMoney(row.allocatedAmount)],
        ['Left on account', formatMoney(row.unappliedAmount)],
      ],
      showCost: true,
    });
    void api.get(`/api/v1/fin/receipts/{id}/print`.replace('{id}', row.id) as never);
  }

  const columns: Array<GridColumn<ReceiptRow>> = [
    { key: 'receivedOn', header: 'Date', width: 104, render: (r) => formatDate(r.receivedOn) },
    { key: 'documentNo', header: 'Receipt no', width: 140, render: (r) => r.documentNo },
    { key: 'buyerName', header: 'Party', width: 170, render: (r) => r.buyerName },
    {
      key: 'entryAmount',
      header: 'Received',
      width: 120,
      align: 'end',
      render: (r) => formatMoney(r.entryAmount),
    },
    { key: 'instrument', header: 'How', width: 110, render: (r) => r.instrument },
    {
      key: 'intoName',
      header: 'Into',
      width: 170,
      render: (r) => r.intoName ?? <span className="muted">—</span>,
    },
    {
      key: 'allocatedAmount',
      header: 'Applied',
      width: 120,
      align: 'end',
      render: (r) => formatMoney(r.allocatedAmount),
    },
    {
      key: 'unappliedAmount',
      header: 'On account',
      width: 120,
      align: 'end',
      render: (r) => formatMoney(r.unappliedAmount),
    },
    { key: 'status', header: 'Status', width: 120, render: (r) => <StatusPill status={r.status} /> },
  ];

  const form = (
    <FormPanel
      title={editing ? `Receipt ${editing.documentNo}` : 'Record money received'}
      icon={<AppIcon kind="paid" size={18} />}
      intro="Who paid, how much, where it went, and which bills it settles."
    >
      <FormGroup title="Who and how much" columns={2}>
        <SearchSelect
          label="Party"
          value={buyerId}
          onChange={(option) => {
            setBuyerId(option?.value ?? '');
            setAllocations({});
          }}
          options={(buyers.data?.items ?? []).map((b) => ({
            value: b.id,
            label: b.nameEn,
            ...(Number(b.closingBalance) > 0
              ? { meta: `owes ${formatMoney(b.closingBalance)}` }
              : {}),
          }))}
          selectedLabel={editing?.buyerName}
          required
          disabled={Boolean(editing)}
        />
        <Field label="Date" type="date" value={receivedOn} onChange={setReceivedOn} required />
        <Field label="Amount" type="number" value={amount} onChange={setAmount} required />
        <Select
          label="How"
          value={instrument}
          onChange={setInstrument}
          options={INSTRUMENTS}
          required
        />
        <Select label="Into" value={into} onChange={setInto} options={places} required />
        <Field
          label="Bank charge"
          type="number"
          value={bankCharge}
          onChange={setBankCharge}
          hint="Kept apart from the amount: the charge is the bank's, not the buyer's."
        />
        {instrument === 'CHEQUE' && (
          <Field label="Cheque no" value={chequeNo} onChange={setChequeNo} mono />
        )}
        <Field label="Reference" value={reference} onChange={setReference} />
      </FormGroup>

      <FormGroup
        title="Which bills this settles"
        hint="Oldest first, which is how a running account is normally cleared."
      >
        <div className="toolbar">
          <button
            type="button"
            className="btn btn--sm"
            disabled={!buyerId || !amount}
            onClick={allocateOldestFirst}
          >
            Allocate oldest first
          </button>
          <button
            type="button"
            className="btn btn--sm btn--ghost"
            onClick={() => setAllocations({})}
          >
            Clear
          </button>
        </div>
        <Table
          count={openBills.data?.items.length ?? 0}
          empty={buyerId ? 'This buyer has nothing outstanding.' : 'Pick a buyer first.'}
          head={
            <tr>
              <th>Bill</th>
              <th>Date</th>
              <th className="num">Outstanding</th>
              <th className="num">Apply</th>
            </tr>
          }
        >
          {(openBills.data?.items ?? []).map((bill) => (
            <tr key={bill.id}>
              <td>{bill.documentNo}</td>
              <td>{formatDate(bill.billDate)}</td>
              <td className="num">{formatMoney(bill.outstanding)}</td>
              <td className="num">
                <Field
                  label=""
                  type="number"
                  value={allocations[bill.id] ?? ''}
                  onChange={(v) => setAllocations((a) => ({ ...a, [bill.id]: v }))}
                />
              </td>
            </tr>
          ))}
        </Table>
        <p className="muted">
          Applied {formatMoney(allocatedTotal)} · left on account{' '}
          {unapplied === null ? '—' : formatMoney(unapplied)}
        </p>
      </FormGroup>

      <FormGroup title="Note">
        <TextArea label="Note" value={remarks} onChange={setRemarks} />
      </FormGroup>

      {error && <ErrorNote message={error} />}

      <FormActions onCancel={() => setShowForm(false)}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={mutate.isPending}
          onClick={() => void save()}
        >
          {editing ? 'Save changes' : 'Save receipt'}
        </button>
      </FormActions>
    </FormPanel>
  );

  return (
    <ScreenWithForm
      canCreate={can('fin.receipt.create')}
      addLabel="Record money received"
      showForm={showForm}
      onToggle={() => {
        if (showForm) return setShowForm(false);
        reset();
        setShowForm(true);
      }}
      form={form}
    >
      <div className="toolbar toolbar--wrap">
        <Select
          label="Status"
          value={status}
          onChange={(v) => {
            setStatus(v);
            setSkip(0);
          }}
          options={STATUSES}
          placeholder="All"
        />
      </div>

      {note && <OkNote message={note} />}
      {error && !showForm && <ErrorNote message={error} />}

      <ActionBar
        actions={[
          {
            label: 'Edit',
            icon: 'edit',
            enabled: Boolean(current && current.status === 'DRAFT' && can('fin.receipt.update')),
            reason: 'Only a draft can be edited',
            onSelect: () => current && startEdit(current),
          },
          {
            label: 'Post',
            icon: 'post',
            primary: true,
            enabled: Boolean(current && current.status === 'DRAFT' && can('fin.receipt.post')),
            reason: 'Only a draft can be posted',
            onSelect: () => current && void post(current),
          },
          {
            label: 'Print',
            icon: 'print',
            enabled: Boolean(current) && can('fin.receipt.print'),
            reason: 'Pick a receipt first',
            onSelect: () => current && print(current),
          },
        ]}
      >
        {current?.status === 'POSTED' && can('fin.receipt.reverse') && (
          <ReverseButton
            label={`Reverse ${current.documentNo}`}
            pending={mutate.isPending}
            onReverse={(reason) => {
              void mutate
                .mutateAsync(() =>
                  api.post(
                    `/api/v1/fin/receipts/${current.id}/reverse?version=${current.version}`,
                    { reason },
                  ),
                )
                .then(() => setNote('Receipt reversed.'))
                .catch((e: unknown) =>
                  setError(errorMessage(e, 'That receipt could not be reversed')),
                );
            }}
          />
        )}
      </ActionBar>

      <DataGrid
        id="fin-receipts"
        label="Receipts"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        selectedKey={selected}
        onSelect={(key) => setSelected(key)}
        onOpen={(row) => row.status === 'DRAFT' && startEdit(row)}
        empty="No receipts yet."
        loading={receipts.isLoading}
      />

      <Pager skip={skip} take={PAGE} total={total} onChange={setSkip} />

      <StatusBar>
        <span>
          {total} receipt{total === 1 ? '' : 's'}
        </span>
      </StatusBar>
    </ScreenWithForm>
  );
}
